import {
    ActivityStatus,
    BykiCard,
    BykiList,
    CJKUtil,
    ConfigService,
    FontSizeConstants,
    LDSLanguage,
    LocaleService,
    normalizeFontName,
    PlayerConfig,
    UniqueCardsSorter,
    UserData
} from '@transparent/coretypes';
import {insertFontCss} from './css-font-util';
import {ActivityVO} from '../vo/activity-vo';
import {from, Observable, of} from 'rxjs';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {normalizePath} from './activity-util';
import {LessonVO} from '../vo/lesson-vo';
import {HttpClient} from '@angular/common/http';
import {LanguagePair} from '../vo/language-pair';
import * as CwApi from '@transparent/cw-api';
import {AssessmentStatus, CompletionStatus} from '@transparent/cw-api';

/**
 * convert activity status
 */
export function convertActivityStatus(status: string): string {
    if (status === ActivityStatus.COMPLETED) {
        return 'COMPLETED';
    } else if (status === ActivityStatus.INCOMPLETE) {
        return 'IN_PROGRESS';
    }
    return 'NOT_STARTED';
}

/**
 * convert activity status
 */
export function convertToCompletionStatus(status: string): CompletionStatus {
    if (status === ActivityStatus.COMPLETED) {
        return CompletionStatus.COMPLETED;
    } else if (status === ActivityStatus.INCOMPLETE) {
        return CompletionStatus.INCOMPLETE;
    }
    return CompletionStatus.NOT_STARTED;
}

/**
 * convert activity status
 */
export function convertToActivityStatus(status: CompletionStatus): string {
    if (status === CompletionStatus.COMPLETED) {
        return ActivityStatus.COMPLETED;
    } else if (status === CompletionStatus.INCOMPLETE) {
        return ActivityStatus.INCOMPLETE;
    }
    return ActivityStatus.NOT_ATTEMPTED;
}

/**
 * convert activity status
 */
export function convertStringToAssessmentStatus(status: string): AssessmentStatus {
    if (status === ActivityStatus.PASSED) {
        return AssessmentStatus.PASSED;
    } else if (status === ActivityStatus.FAILED) {
        return AssessmentStatus.FAILED;
    }
    return AssessmentStatus.NOT_STARTED;
}

/**
 * convert activity status
 */
export function convertAssessmentStatusToString(status: AssessmentStatus): string {
    if (status === AssessmentStatus.PASSED) {
        return ActivityStatus.PASSED;
    } else if (status === AssessmentStatus.FAILED) {
        return ActivityStatus.FAILED;
    }
    return ActivityStatus.NOT_ATTEMPTED;
}

export function convertActivitiesToStatusObject(activities: ActivityVO[]): CwApi.ActivityStatus[] {
    return activities?.map((act) => {
        return {
            passed: act.status === ActivityStatus.COMPLETED,
            module: act.moduleName,
            uuid: act.uuid,
            optional: !act.required
        };
    });
}

/**
 * Goes to mone home page if available
 */
export function goHome(options?: string): void {
    if (typeof window.CWAPI?.main?.goHome === 'function') {
        window.CWAPI.main.goHome(options);
    }
}

/**
 * Goes to mone home page if available
 */
export function sendEvent(lang?: { known: string, target: string }): void {
    if (typeof window.CWAPI?.eventTracker?.onGrammarReferenceAccess === 'function') {
        window.CWAPI.eventTracker.onGrammarReferenceAccess(lang).catch((err) => console.log(err));
    }
}

/**
 * Goes to mone home page if available
 * {boolean}
 */
export function goMobile(upConfig: PlayerConfig): void {
    if (upConfig.goMobileAvailable) {
        /*const api = getAPI();
        if (api && typeof api.goMobile === 'function') {
            api.goMobile();
        }*/
    }
}

/**
 * Returns shareable link
 * {string}
 */
export function getInitialLink(href: string): string {
    const activityPat = /lesson\/(\d+)\/activity\/(\d+)/;
    const assessmentPat = /lesson\/(\d+)\/assessment\/\d+/;
    let matches: string[];
    if (/unit-objective/.test(href)) {
        return 'unit-objective';
    } else if (activityPat.test(href)) {
        matches = href.match(activityPat) || ['', '0', '0']; // just as doc, should never happen
        return `lesson/${matches[1]}/activity/${matches[2]}`;
    } else if (assessmentPat.test(href)) {
        matches = href.match(assessmentPat) || ['', '0']; // just as doc, should never happen
        return `lesson/${matches[1]}/assessment/0`;
    }
    return '';
}

/**
 * Saves alternative answers
 */
export function saveAltAnswer(data: any): void {
    if (window.CWAPI?.altAnswer) {
        window.CWAPI.altAnswer.add(data.hid, data.mode, data.phrase).catch((err) => console.log(err));
    }
}

/**
 * Saves user settings
 * {Object} data
 */
export function saveUserSettings(data: UserData): void {
    if (window.CWAPI?.user && data) {
        window.CWAPI.user.saveUserData(data).catch((err) => console.log(err));
    }
}

/**
 * Set selected activity index to update shareable link in TLO
 * {number} activityIndex Selected activity index
 *  unit service line 595
 */
export function sendActivityIndexToTlo(activityIndex: number, info: CwApi.ActivityInfo): void {
    if (typeof window.CWAPI?.main?.setActivity === 'function') {
        window.CWAPI.main.setActivity(activityIndex, info).catch();
    }
}

/**
 * Parses configuration info from unit xml
 */
export function setConfigAndCss(http: HttpClient, config: ConfigService, locale: LocaleService, lang: LanguagePair): Observable<any> {
    const defaultLang: LDSLanguage = {} as LDSLanguage;
    const uiLoc = locale.getLanguageByLocaleCode(locale.localeName) || defaultLang;
    config.knownLanguageCode = lang.known;
    config.learningLanguageCode = lang.target;
    config.uiLanguageCode = uiLoc.code;
    config.uiDirection = uiLoc.dir;
    config.isTransliterated = config.learningLanguageCode.substr(0, 2) === 'T_'
        || config.learningLanguageCode === 'CANTONESE';
    if (config.learningLanguageCode === 'KURDISH_BEHDINI_LATIN') {
        config.isTransliterated = true;
        config.learningLanguageCode = config.learningLanguageCode.replace('_LATIN', '');
    }
    if (config.isTransliterated && config.learningLanguageCode.substr(0, 2) !== 'T_') {
        config.translitLanguageCode = config.learningLanguageCode;
        config.learningLanguageCode = 'T_' + config.learningLanguageCode;
    } else if (config.isTransliterated && config.learningLanguageCode) {
        config.translitLanguageCode = config.learningLanguageCode.substr(2);
    }
    config.translitFontFamily = 'Latin';
    if (config.learningLanguageCode === 'T_MANDARIN') {
        config.learningLanguageCode = 'T_CHINESE';
    }
    const knownLoc = locale.lds[config.knownLanguageCode] || defaultLang;
    const learnLoc = locale.lds[config.learningLanguageCode] || defaultLang;
    const translitLoc = locale.lds[config.translitLanguageCode || 'ENGLISH'] || learnLoc || defaultLang;
    config.uiDirection = uiLoc.dir || 'ltr';
    config.learningDirection = learnLoc.dir || 'ltr';
    config.learningLanguage = learnLoc.shortName || learnLoc.name;
    config.knownDirection = knownLoc.dir || 'ltr';
    config.knownLanguage = knownLoc.shortName || knownLoc.name;
    config.hasScriptAssistant = learnLoc.hasScriptAssistant || CJKUtil.isCK(config.learningLanguageCode);
    config.uiFontFamily = normalizeFontName(uiLoc.font) || 'Latin';
    config.knownFontFamily = normalizeFontName(knownLoc.font) || 'Latin';
    config.learningFontFamily = normalizeFontName(learnLoc.font) || 'Latin';
    config.translitFontFamily = normalizeFontName(translitLoc.font) || 'Latin';
    config.knownFontSize = FontSizeConstants.getDefault(config.knownFontFamily);
    config.learningFontSize = FontSizeConstants.getDefault(config.learningFontFamily);
    config.translitFontSize = FontSizeConstants.getDefault(config.translitFontFamily);
    config.easyType = learnLoc.easyType;
    config.asr = learnLoc.asr;
    config.knownEasyType = knownLoc.easyType;
    if (CJKUtil.isCK(config.learningLanguageCode)) {
        locale.props.txtImeBody = (locale.props.txtImeBody || '').replace(/{CKLanguage}/g, config.learningLanguage);
    } else if (CJKUtil.isCK(config.knownLanguageCode)) {
        locale.props.txtImeBody = (locale.props.txtImeBody || '').replace(/{CKLanguage}/g, config.knownLanguageCode);
    }
    insertFontCss(uiLoc.font || 'Latin', 'uiFont',
        config.uiDirection, uiLoc.code, config.fontUrl);
    insertFontCss(knownLoc.font || 'Latin', 'knownFont',
        knownLoc.dir, config.knownLanguageCode, config.fontUrl);
    insertFontCss(learnLoc.font || 'Latin', 'learningFont',
        learnLoc.dir, config.learningLanguageCode, config.fontUrl);
    insertFontCss(translitLoc.font || 'Latin', 'translitFont',
        translitLoc.dir, config.translitLanguageCode, config.fontUrl);
    if (!config.isTransliterated && config.hasScriptAssistant && !CJKUtil.isChinese(config.learningLanguageCode)) {
        config.hasScriptAssistant = false;
        return checkAndGetAlphabetUrl(config.upConfig, http, lang)
            .pipe(map((alphabetData: any) => {
                if (!alphabetData.alphabetUrl && config.upConfig.isReference && config.upConfig.activityName) {
                    config.alphabetURL = normalizeAlphabetPath(config.href, config.upConfig.alphabetPath);
                } else {
                    config.alphabetURL = normalizeAlphabetPath(config.href, alphabetData.alphabetUrl);
                }
                config.hasScriptAssistant = alphabetData.hasScriptAssistant;
            }));
    }

    let arabicFont = 'Arabic';
    if (knownLoc.dir === 'rtl') {
        arabicFont = knownLoc.font;
    } else if (learnLoc.dir === 'rtl') {
        arabicFont = learnLoc.font;
    } else if (uiLoc.dir === 'rtl') {
        arabicFont = uiLoc.font;
    } else if (translitLoc.dir === 'rtl') {
        arabicFont = translitLoc.font;
    }
    config['arabicFont'] = arabicFont;
    config.alphabetURL = normalizeAlphabetPath(config.href, config.upConfig.alphabetPath);
    return of('');
}

/**
 * Parses configuration info from unit xml
 */
export function setKnownAndLearningCss(knownLanguageCode: string,
                                       learningLanguageCode: string,
                                       locale: LocaleService,
                                       fontUrl: string): void {
    const knownLoc = locale.lds[knownLanguageCode] || {} as LDSLanguage;
    const learnLoc = locale.lds[learningLanguageCode] || {} as LDSLanguage;
    if (CJKUtil.isCK(learningLanguageCode)) {
        locale.props.txtImeBody = (locale.props.txtImeBody || '').replace(/{CKLanguage}/g, learningLanguageCode);
    } else if (CJKUtil.isCK(knownLanguageCode)) {
        locale.props.txtImeBody = (locale.props.txtImeBody || '').replace(/{CKLanguage}/g, knownLanguageCode);
    }
    insertFontCss(knownLoc.font || 'Latin', 'knownFont',
        knownLoc.dir, knownLanguageCode, fontUrl);
    insertFontCss(learnLoc.font || 'Latin', 'learningFont',
        learnLoc.dir, learningLanguageCode, fontUrl);
}

/**
 * Set sound and image paths using media resolver callback
 * {BykiList} list List with cards to set assets path for
 * {Function} resolver Callback method which sets full path to assets
 */
export function setAssetPath(list: BykiList, resolver: (value: string) => string): void {
    if (typeof resolver !== 'function') {
        return;
    }
    const cards = list.cards;
    const len = cards.length;
    for (let i = 0; i < len; i++) {
        const card = cards[i];
        if (card.soundSide1) {
            card.soundSide1 = resolver(card.soundSide1);
        }
        if (card.soundSide2) {
            card.soundSide2 = resolver(card.soundSide2);
        }
        if (card.picture) {
            card.picture = resolver(card.picture);
        }
    }
}

/**
 * If it is relative add href as base
 * {string} baseUrl
 * {string} alphabetUrl
 * {string}
 */
function normalizeAlphabetPath(baseUrl: string, alphabetUrl: string): string {
    alphabetUrl = normalizePath(alphabetUrl || 'alphabet/');
    const ap = /^(http|https|file)?(:)?\/\//;
    if (!ap.test(alphabetUrl)) {
        return baseUrl + alphabetUrl;
    }
    return alphabetUrl;
}

/**
 * Cheks if thers is data fir activity
 * {BykiCard[]} cards
 * {string} actName
 * {boolean}
 */
export function hasData(cards: BykiCard[], actName = ''): boolean {
    actName = actName.replace(/modules\//, '')
        .replace('.swf', '')
        .replace(/A$/, '')
        .toLowerCase();
    if (actName === 'unscramble') {
        cards = UniqueCardsSorter.getUnscrambleCards(cards);
    } else if (actName === 'audiomultichoice' || actName === 'listeningrecognition') {
        cards = UniqueCardsSorter.getAudioCards(cards);
    }
    return cards.length > 1;
}

/**
 * Gets activity score
 * moduleName
 * {number}
 */
export function getActivityScore(moduleName: string): number {
    switch (moduleName) {
        case 'Preview':
        case 'SignPreview':
            return 1;
        case 'SelfReportingRecognize':
        case 'RecognizeSign':
            return 4;
        case 'RecognizeWritten':
        case 'SignWritten':
            return 5;
        case 'SelfReportingProduce':
        case 'ProduceSign':
            return 7;
        case 'WrittenProduce':
            return 8;
        default:
            return 10;
    }
}

/**
 * {ActivityVO[]} activities
 * {BykiCard[]} cards
 * {ActivityVO[]}
 */
export function getAssessmentActs(activities: ActivityVO[], cards: BykiCard[]): ActivityVO[] {
    return activities.filter((act: ActivityVO) => hasData(cards, act.moduleURL));
}

/**
 * Gets alphabet url
 * {String}
 */
export function checkAndGetAlphabetUrl(appConfig: PlayerConfig,
                                       http: HttpClient,
                                       lang: { known: string, target: string }): Observable<any> {
    let alphabetUrl = '';
    return getAlphabetPath(appConfig, lang)
        .pipe(mergeMap((url: string) => {
                alphabetUrl = url;
                const tPat = /^T_/ig;
                const alphabetName = (lang.target || 'alphabet')
                    .replace(tPat, '')
                    .toLowerCase();
                url = url + alphabetName + '.xml';
                return http.get(url, {responseType: 'text'});
            }),
            catchError(() => of(false)),
            map((value: any) => {
                return {
                    hasScriptAssistant: value !== false,
                    alphabetUrl
                };
            }));
}

/**
 *  Gets alphabet url if it is mobile app
 */
function getAlphabetPath(appConfig: PlayerConfig, lang: { known: string, target: string }): Observable<any> {
    if (window.CWAPI?.languages) {
        return from(window.CWAPI.languages.hasAlphabet(lang))
            .pipe(mergeMap((hasAlphabet: boolean) => {
                return hasAlphabet
                    ? from(window.CWAPI.languages.getAlphabetURL(lang))
                        .pipe(map((alphabet: string) => normalizePath(alphabet)))
                    : of('');
            }));
    } else {
        const alphabetUrl = normalizePath(appConfig.alphabetPath || 'alphabet/');
        return of(alphabetUrl);
    }
}

export function getActivityRouteUrl(lesson: LessonVO, actIndex = 0): string {
    const actName = lesson.isAssessment ? 'assessment' : 'activity';
    // act index for assessment is always 0
    actIndex = !lesson.isAssessment ? Math.max(0, actIndex) : 0;
    return `lesson/${lesson.index}/${actName}/${actIndex}`;
}
